import * as React from "react";
import { splitEscaped } from "./split";

// Pass/fail grid for a benchmark: one table row per instance, one cell per
// run (filled + check = passed, outlined dot = failed), plus a "k/n" count.
// Rows are sorted by passes, most first (stable), with a thin rule between the
// all-pass, partial and zero groups.
//
// Props match the MDX registry entry:
// `labels` comma-separated instance names (e.g. pytest-dev__pytest-5631);
//          blank segments stay positional and show "—"; write "\," for a
//          literal comma inside a name
// `passes` passes per instance, 0..runs (clamped)
// `runs`   runs per instance (clamped to 0..100)
// `cells`  optional, comma-separated per-instance run outcomes aligned with
//          `labels`, one char per run ("1" passed, anything else failed), e.g.
//          "110,000,111". Without it the passed runs are drawn first; with it
//          the grid shows WHICH runs passed, so clustering stays visible.
//
// If labels and passes differ in length the grid renders max(both) rows; a
// row with a label but no pass count shows an explicit "no data" marker and
// is left out of the totals, and a count with no label is named "(unnamed)".

export interface PassGridProps {
  labels: string;
  passes: number[];
  runs: number;
  cells?: string;
}

const MAX_RUNS = 100;

const on = (r: Row, k: number) => (r.pattern !== null ? r.pattern[k] === "1" : k < (r.passed ?? 0));

// Split "org__repo-123" so the org prefix can render small above the name.
function splitName(name: string): { prefix: string; main: string } {
  const at = name.indexOf("__");
  if (at > 0 && at + 2 < name.length) return { prefix: name.slice(0, at + 2), main: name.slice(at + 2) };
  return { prefix: "", main: name };
}

type Group = "all" | "some" | "none" | "nodata";

interface Row {
  name: string;
  passed: number | null;
  group: Group;
  pattern: string | null;
}

export function PassGrid({ labels, passes, runs, cells }: PassGridProps) {
  const names = (labels ?? "") === "" ? [] : splitEscaped(labels ?? "", ",").map((s) => s.trim());
  const nRuns = Number.isFinite(runs) ? Math.min(Math.max(0, Math.floor(runs)), MAX_RUNS) : 0;
  const list = passes ?? [];
  const patterns = (cells ?? "") === "" ? [] : splitEscaped(cells ?? "", ",").map((s) => s.trim());
  const count = Math.max(names.length, list.length);

  const built: Row[] = Array.from({ length: count }, (_, i) => {
    const name = i < names.length ? names[i] || "—" : "(unnamed)";
    const raw = list[i];
    if (typeof raw !== "number" || !Number.isFinite(raw)) return { name, passed: null, group: "nodata", pattern: null };
    const p = Math.min(Math.max(0, Math.round(raw)), nRuns);
    // A pattern is only trusted when it agrees with the pass count; otherwise
    // fall back to the count-only layout instead of drawing a contradiction.
    const pat = patterns[i];
    const usable = pat !== undefined && pat.length === nRuns && [...pat].filter((c) => c === "1").length === p;
    return { name, passed: p, group: p === 0 ? "none" : p === nRuns ? "all" : "some", pattern: usable ? pat : null };
  });
  // Array.prototype.sort is stable: ties keep their authored order.
  const rows = [...built].sort((a, b) => (b.passed ?? -1) - (a.passed ?? -1));

  const withData = rows.filter((r) => r.passed !== null);
  const totalPass = withData.reduce((a, r) => a + (r.passed ?? 0), 0);
  const totalRuns = withData.length * nRuns;
  const missing = rows.length - withData.length;
  const missingNote = missing ? `, ${missing} ${missing === 1 ? "instance has" : "instances have"} no data` : "";

  return (
    <div className="gc-widget pg-widget">
      {/* The caption below is the one announcement; this visible label is hidden from AT. */}
      <div className="gc-widget-label" aria-hidden="true">
        {"// "}pass grid, {totalPass}/{totalRuns} runs passed{missingNote}
      </div>
      <table className="pg-table">
        <caption className="viz-sr">
          Passes per instance, {nRuns} runs each, most passes first; {totalPass} of {totalRuns} passed in total
          {missingNote}
        </caption>
        <thead>
          <tr>
            <th scope="col">instance</th>
            <th scope="col">run results</th>
            <th scope="col" className="pg-count-h">
              passed
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const { prefix, main } = splitName(r.name);
            const groupStart = i > 0 && rows[i - 1]!.group !== r.group;
            return (
              <tr key={i} className={"pg-row" + (groupStart ? " grp-start" : "")}>
                <th scope="row">
                  {prefix && <span className="pg-prefix">{prefix}</span>}
                  <span className="pg-main">{main}</span>
                </th>
                <td>
                  {r.passed === null ? (
                    <span className="pg-nodata">no data</span>
                  ) : (
                    <>
                      <span className="viz-sr">
                        {r.passed} of {nRuns} runs passed
                        {r.pattern !== null &&
                          ` (${[...r.pattern].map((c, k) => `run ${k + 1} ${c === "1" ? "passed" : "failed"}`).join(", ")})`}
                      </span>
                      <span className="pg-cells" aria-hidden="true">
                        {Array.from({ length: nRuns }, (_, k) => (
                          <span key={k} className={"pg-cell" + (on(r, k) ? " on" : "")}>
                            {on(r, k) ? "✓" : "·"}
                          </span>
                        ))}
                      </span>
                    </>
                  )}
                </td>
                <td className="pg-count">{r.passed === null ? "n/a" : `${r.passed}/${nRuns}`}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="gc-legend" aria-hidden="true">
        <span>
          <span className="pg-cell on">✓</span>passed run
        </span>
        <span>
          <span className="pg-cell">·</span>failed run
        </span>
      </div>
    </div>
  );
}

export default PassGrid;
